// /ops — case list + create.
// A "case" is an investigation workspace that scopes opportunities, watchlists,
// bids, and notes under one banner (e.g. CASE_F35_BLK4). Header CASE selector
// switches the active case (Phase 1 wires that selector to this list).

import { ApiError, apiFetch } from '@/lib/api';
import { relTime, ts } from '@/lib/format';
import { EmptyState, Icon, ObjectHeader, Pill, type Tone } from '@/primitives';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_authed/ops')({
  component: CasesPage,
});

interface CaseRow {
  id: string;
  name: string;
  slug: string;
  classification: 'PUBLIC' | 'CUI' | 'SECRET' | 'TS' | 'TS_SCI';
  cage_code: string | null;
  banner_label: string | null;
  status: 'open' | 'active' | 'archived' | 'closed';
  created_at: string;
  updated_at: string;
}

function classTone(c: CaseRow['classification']): Tone {
  switch (c) {
    case 'TS':
    case 'TS_SCI':
      return 'red';
    case 'SECRET':
      return 'green';
    case 'CUI':
      return 'amber';
    default:
      return 'gray';
  }
}

function CasesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const cases = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiFetch<{ items: CaseRow[] }>('/v1/cases'),
  });
  const items = cases.data?.items ?? [];

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {cases.isFetching && <div className="progress" />}

      <ObjectHeader
        type="CASE"
        id="WORKSPACE"
        title="Cases"
        sub="Investigation workspaces — opportunities, bids, and notes scoped under one banner"
        pills={[{ tone: 'primary', label: `${ts(items.length)} OPEN` }]}
        actions={
          <button type="button" className="btn sm pri" onClick={() => setShowNew(true)}>
            <Icon name="plus" size={12} />
            NEW CASE
          </button>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!cases.isLoading && items.length === 0 ? (
          <EmptyState
            glyph="CASE"
            reason="No cases yet"
            detail="A case bundles related opportunities, bids, and watchlists. Create your first one to scope the workspace."
            actions={
              <button type="button" className="btn pri" onClick={() => setShowNew(true)}>
                CREATE FIRST CASE
              </button>
            }
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>NAME</th>
                <th>SLUG</th>
                <th>CLASSIFICATION</th>
                <th>STATUS</th>
                <th>CAGE</th>
                <th className="r">UPDATED</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  style={{ cursor: 'pointer' }}
                  // /cases/$id detail page lands in next push — for now stay on /cases
                  onClick={() => {
                    /* navigate({ to: '/cases/$id', params: { id: c.id } }) */ void c.id;
                  }}
                >
                  <td>{c.name}</td>
                  <td className="id mn">{c.slug}</td>
                  <td>
                    <Pill tone={classTone(c.classification)} dot={false}>
                      {c.classification}
                    </Pill>
                  </td>
                  <td className="mn dim">{c.status}</td>
                  <td className="mn">{c.cage_code ?? '—'}</td>
                  <td className="r mn dim">{relTime(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewCaseDialog
          onClose={() => setShowNew(false)}
          onCreated={(_id) => {
            setShowNew(false);
            void qc.invalidateQueries({ queryKey: ['cases'] });
            // /cases/$id is not yet routed — the new case appears in the list
            // above. When the case-detail route lands, navigate({ to: "/cases/$id", params: { id } }).
          }}
        />
      )}
    </div>
  );
}

function NewCaseDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [classification, setClassification] = useState<CaseRow['classification']>('PUBLIC');
  const [cage, setCage] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ id: string }>('/v1/cases', { method: 'POST', body }),
    onSuccess: (data: { id: string }) => onCreated(data.id),
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.code : 'internal_error'),
  });

  const computedSlug =
    slug ||
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 30 }}
      />
      <div
        role="dialog"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 460,
          background: 'var(--bg-pane)',
          border: '1px solid var(--bd-strong)',
          padding: 20,
          zIndex: 31,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="h-strong upper">NEW CASE</span>
          <button className="btn xs gh" onClick={onClose} type="button">
            <Icon name="x" size={10} />
          </button>
        </div>

        {err && (
          <div
            style={{
              padding: '6px 10px',
              border: '1px solid var(--red-line)',
              background: 'var(--red-soft)',
              color: 'var(--red)',
              fontFamily: 'var(--mono)',
              fontSize: 11,
            }}
          >
            {err}
          </div>
        )}

        <Field label="NAME">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            style={fieldStyle()}
          />
        </Field>
        <Field label="SLUG" hint="URL identifier · auto-derived if blank">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            placeholder={computedSlug}
            style={{ ...fieldStyle(), fontFamily: 'var(--mono)' }}
          />
        </Field>
        <Field label="CLASSIFICATION">
          <select
            value={classification}
            onChange={(e) => setClassification(e.target.value as CaseRow['classification'])}
            style={{ ...fieldStyle(), fontFamily: 'var(--mono)' }}
          >
            <option value="PUBLIC">PUBLIC</option>
            <option value="CUI">CUI</option>
            <option value="SECRET">SECRET</option>
            <option value="TS">TS</option>
            <option value="TS_SCI">TS//SCI</option>
          </select>
        </Field>
        <Field label="CAGE CODE" hint="optional · vendor identifier">
          <input
            type="text"
            value={cage}
            onChange={(e) => setCage(e.target.value.toUpperCase().slice(0, 16))}
            style={{ ...fieldStyle(), fontFamily: 'var(--mono)' }}
          />
        </Field>

        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            type="button"
            className="btn pri"
            disabled={!name || create.isPending}
            onClick={() =>
              create.mutate({
                name: name.trim(),
                slug: computedSlug,
                classification,
                cage_code: cage || undefined,
              })
            }
            style={{ flex: 1, justifyContent: 'center', height: 30 }}
          >
            {create.isPending ? 'CREATING…' : 'CREATE CASE'}
          </button>
          <button
            type="button"
            className="btn gh"
            onClick={onClose}
            style={{ height: 30, padding: '0 12px' }}
          >
            CANCEL
          </button>
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  hint,
  children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      {children}
      {hint && (
        <span className="meta" style={{ fontSize: 10 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function fieldStyle(): React.CSSProperties {
  return {
    height: 28,
    padding: '0 8px',
    background: 'var(--bg-base)',
    border: '1px solid var(--bd-strong)',
    color: 'var(--fg-1)',
    fontSize: 12,
  };
}
